import {useCallback, useRef, useState} from 'react'

const EXTENSION_MIME_MAP = {
  csv: ['text/csv', 'application/vnd.ms-excel'],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/octet-stream'],
}
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024
const FILE_SIZE_ERROR_MESSAGE = 'File size must be 5 MB or less.'

export const isValidFile = (file, acceptedExtensions = '.csv') => {
  if (!file) return false
  const fileName = String(file?.name || '').toLowerCase()
  const mimeType = String(file?.type || '').toLowerCase()
  const extensions = acceptedExtensions.split(',').map((ext) => ext.trim().toLowerCase().replace(/^\./, ''))

  const hasValidExtension = extensions.some((ext) => fileName.endsWith(`.${ext}`))
  if (hasValidExtension) return true

  const hasValidMimeType = extensions.some((ext) =>
    (EXTENSION_MIME_MAP[ext] || []).includes(mimeType),
  )
  return hasValidMimeType
}

const useUploadCsvModal = ({onUpload, onClose, invalidFileErrorMessage, validateFile, acceptedExtensions = '.csv'}) => {
  const [selectedFile, setSelectedFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [fileValidationError, setFileValidationError] = useState('')
  const [apiErrors, setApiErrors] = useState(null)
  const fileUploadWrapperRef = useRef(null)

  const handleFileSelection = useCallback(
    async (filePayload) => {
      const nextFile = Array.isArray(filePayload) ? filePayload?.[0] : filePayload
      if (!nextFile) {
        setSelectedFile(null)
        setFileValidationError('')
        return
      }

      if (!isValidFile(nextFile, acceptedExtensions)) {
        setSelectedFile(null)
        setFileValidationError(invalidFileErrorMessage)
        return
      }

      if (nextFile.size > MAX_FILE_SIZE_BYTES) {
        setSelectedFile(null)
        setFileValidationError(FILE_SIZE_ERROR_MESSAGE)
        return
      }

      if (validateFile) {
        const validationError = await validateFile(nextFile)
        if (validationError) {
          setSelectedFile(null)
          setFileValidationError(validationError)
          return
        }
      }

      setSelectedFile(nextFile)
      setFileValidationError('')
      setApiErrors(null)
    },
    [invalidFileErrorMessage, validateFile, acceptedExtensions],
  )

  const handleUpload = useCallback(async () => {
    if (!selectedFile || isUploading) return
    if (!isValidFile(selectedFile, acceptedExtensions)) {
      setSelectedFile(null)
      setFileValidationError(invalidFileErrorMessage)
      return
    }
    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      setSelectedFile(null)
      setFileValidationError(FILE_SIZE_ERROR_MESSAGE)
      return
    }

    setIsUploading(true)
    try {
      const result = await onUpload(selectedFile)
      if (result && result.success === false) {
        setApiErrors(result.errors || ['Upload failed. Please try again.'])
      } else if (result !== false) {
        setSelectedFile(null)
        setApiErrors(null)
        onClose()
      }
    } catch {
      // onUpload is responsible for its own error toasts; just keep modal open
    } finally {
      setIsUploading(false)
    }
  }, [selectedFile, isUploading, onUpload, onClose, invalidFileErrorMessage, acceptedExtensions])

  const handleClose = useCallback(() => {
    setSelectedFile(null)
    setFileValidationError('')
    setApiErrors(null)
    onClose()
  }, [onClose])

  const handleDelete = useCallback(() => {
    setSelectedFile(null)
    setFileValidationError('')
    setApiErrors(null)
  }, [])

  const openFilePicker = useCallback(() => {
    const fileInput = fileUploadWrapperRef.current?.querySelector('input[type="file"]')
    if (fileInput && !fileInput.disabled) {
      fileInput.click()
    }
  }, [])

  const handleUploadZoneClick = useCallback(
    (event) => {
      const targetElement = event.target instanceof Element ? event.target : null
      const interactiveTarget = targetElement?.closest('button, input, label, a')
      if (interactiveTarget) return
      openFilePicker()
    },
    [openFilePicker],
  )

  const handleUploadZoneKeyDown = useCallback(
    (event) => {
      const isEnter = event.key === 'Enter'
      const isSpace = event.key === ' '
      if (!isEnter && !isSpace) return
      event.preventDefault()
      openFilePicker()
    },
    [openFilePicker],
  )

  const handleUploadZoneTouchEnd = useCallback(
    (event) => {
      const targetElement = event.target instanceof Element ? event.target : null
      const interactiveTarget = targetElement?.closest('button, input, label, a')
      if (interactiveTarget) return
      openFilePicker()
    },
    [openFilePicker],
  )

  const handleFileUploadError = useCallback(() => {
    setSelectedFile(null)
    setFileValidationError(invalidFileErrorMessage)
  }, [invalidFileErrorMessage])

  return {
    selectedFile,
    isUploading,
    fileValidationError,
    apiErrors,
    fileUploadWrapperRef,
    handleFileSelection,
    handleUpload,
    handleClose,
    handleDelete,
    handleUploadZoneClick,
    handleUploadZoneKeyDown,
    handleUploadZoneTouchEnd,
    handleFileUploadError,
  }
}

export default useUploadCsvModal
